// Screen text for S31 First run + S32 Unlock (I-1a Batch 4, IP2-29 (1)).
//
// English PLACEHOLDER copy, kept in one file so I-1b can move it into the signed
// content packages (Hebrew, the three Hebrew forms, the language switch) without
// touching the screens. The product name (D-14) is still the placeholder "Pebble"
// and is never "feelings". Any change to the intended-use, 18+ or privacy text
// is a material change (Tool Plan §7.3, §15.3 rule 9): it goes to the Tool Plan
// first (IM-D-08). No help-line number appears here: the numbers live only in
// content/help/package.yaml (→ app/safety/helpPackage.generated.ts, Batch 5).

import type { BackupRefusal } from '../export'
import type { SecretKind, SecretRefusal } from '../lock'
import type { HelpItem } from '../safety/help'

export const PRODUCT_NAME = 'Pebble'

export const common = {
  back: 'Back',
  continue: 'Continue',
  opening: 'Opening…',
  show: 'Show',
  tryAgainLater: 'Something went wrong. Please try again.',
} as const

export const installGuard = {
  title: `Add ${PRODUCT_NAME} to your home screen`,
  why: 'To keep the diary on this phone, add the app to the home screen first, then open it from there.',
  androidSteps: 'Android (Chrome): open the browser menu (⋮), choose “Add to home screen” or “Install app”.',
  iphoneSteps: 'iPhone (Safari): tap Share, then “Add to Home Screen”.',
  iphoneStorage:
    'On iPhone, a browser tab and the home-screen app keep separate storage, and removing the icon removes the diary.',
  lookAround: 'Just look around',
  lookAroundTitle: 'Looking around',
  lookAroundText: 'Nothing you do here is kept. To start a diary, add the app to your home screen.',
} as const

export const language = {
  title: 'Language',
  text: 'English, following your phone. Hebrew comes in a later version.',
} as const

export const intendedUse = {
  title: `What ${PRODUCT_NAME} is`,
  summary: `${PRODUCT_NAME} is a private diary for noting moments and how they felt, in your own words.`,
  readAll: 'Read all',
  details: [
    'It keeps what you write on this phone. It does not give advice, judge, or draw conclusions.',
    'It is not a medical device and does not replace care from a professional.',
  ],
  notEmergency: 'It is not an emergency service.',
  adultsOnly: `${PRODUCT_NAME} is for adults.`,
  confirmAdult: 'I am 18 or older',
} as const

export const privacy = {
  title: 'Your privacy',
  points: [
    'Everything stays on this phone.',
    'Nothing is shared or sent anywhere.',
    'The diary is locked with a passphrase or PIN that only you know.',
    'No app can protect against someone who knows your lock, or who copies this phone’s storage and has time to guess a short PIN.',
  ],
} as const

export const setLock = {
  title: 'Choose a lock',
  newLockTitle: 'Choose a new lock',
  newLockText: 'The recovery code opened the diary. Choose a new lock for next time.',
  kindLegend: 'Kind of lock',
  kinds: {
    passphrase: 'Passphrase (recommended): at least 10 characters',
    digits: '8 or more digits',
    pin6: '6-digit PIN',
  } satisfies Record<SecretKind, string>,
  pinLimit:
    'Honest limit: a 6-digit PIN protects against a quick look and a lost phone with its own screen lock. Someone who copies this phone’s storage could guess a 6-digit PIN.',
  field: { passphrase: 'Passphrase', digits: 'Digits', pin6: 'PIN' } satisfies Record<SecretKind, string>,
  again: 'Type it again',
  mismatch: 'The two entries are different. Please type it again.',
  wrongKind: 'This does not match the kind of lock you chose above.',
  refusals: {
    'too-short': {
      passphrase: 'Please use at least 10 characters.',
      digits: 'Please use at least 8 digits.',
      pin6: 'A PIN here has 6 digits.',
    },
    'digits-length': 'Please use 6 digits, or 8 or more.',
    repeated: 'Repeating patterns are easy to guess. Please choose another.',
    sequence: 'Runs like 1234 or abcd are easy to guess. Please choose another.',
    common: 'This one is very common, so it is easy to guess. Please choose another.',
    date: 'This looks like a date, and dates are easy to guess. Please choose another.',
  } satisfies Record<SecretRefusal, string | Record<SecretKind, string>>,
} as const

export const recovery = {
  title: 'If you forget the lock',
  text: 'A recovery code can open the diary if you forget your lock. It is long and random. Write it down or save it somewhere only you can reach. It never leaves this phone.',
  make: 'Make a recovery code',
  skip: 'Not now',
  codeLabel: 'Your recovery code',
  saved: 'I have written it down',
  backup:
    'If this phone is lost, only a backup file can bring the diary back. You will be offered one after your first moment.',
} as const

export const unlock = {
  field: 'Passphrase or PIN',
  open: 'Open',
  wrong: 'It did not open. You can try again.',
  wait: (seconds: number): string =>
    seconds === 1 ? 'You can try again in 1 second.' : `You can try again in ${seconds} seconds.`,
  damaged: 'This phone’s lock data cannot be read. See “I can’t open it”.',
  cantOpen: 'I can’t open it',
  cantOpenTitle: 'If you can’t open it',
  cantOpenText:
    'The diary opens only with your lock, or with the recovery code if you made one. With a backup file and its passphrase, the diary can be restored into a new one.',
  useRecovery: 'Use the recovery code',
  recoveryField: 'Recovery code',
  notACode: 'That does not look like a recovery code. It has 28 letters and numbers.',
  deleteAll: 'Delete everything and start again',
  confirmTitle: 'Delete everything?',
  confirmText: 'This deletes the diary and its lock on this phone. It cannot be brought back.',
  confirmCheck: 'I understand it cannot be brought back',
  confirmDelete: 'Delete everything',
  keep: 'Keep my diary',
  closeOthers: `${PRODUCT_NAME} is open in another window. Please close it to finish.`,
} as const

export const home = {
  text: 'The diary is open. Its screens come in a later version.',
  failedTries: (count: number): string =>
    count === 1 ? '1 unsuccessful try since the last unlock.' : `${count} unsuccessful tries since the last unlock.`,
  readOnly: `${PRODUCT_NAME} is open in another window, so here it is read-only.`,
  // I-1a Batch 7 (Plan §6.3, §15.4): the store opened read-only for a version reason.
  migrationFailed: `${PRODUCT_NAME} could not finish an update to how your diary is stored. Your diary is safe and unchanged. For now nothing new can be saved; you can still make a backup with “Backup & move”.`,
  newerData: `This diary was saved by a newer version of ${PRODUCT_NAME}. It is safe; here it is read-only. Update ${PRODUCT_NAME} to keep writing, or make a backup with “Backup & move”.`,
  storageFull:
    'This phone is out of space, so nothing new can be saved for now. What is already saved stays safe. Free some space on the phone, or make a backup with “Backup & move”.',
  lockNow: 'Lock now',
} as const

// S33 Backup & move + the S31 restore path (I-1a Batch 6b; Implementation Plan
// v02 §6.4; designed in code first, D-80). The honest lines (IR-24) say where a
// file may be restored, that "Delete everything" does not reach saved files,
// and what the file's passphrase means.
export const backup = {
  open: 'Backup & move',
  title: 'Backup & move',
  what: 'A backup file holds the whole diary, locked with a backup passphrase. With it, the diary can be restored on a new phone, or on this one after it is reset.',
  reminder: {
    never: 'No backup file has been made on this phone yet.',
    old: 'The last backup file made on this phone is more than 14 days old.',
  },
  passphrase: 'Backup passphrase',
  passphraseHint: 'At least 12 characters, or 5 words. It may be the same as your lock, but you type it again here.',
  again: 'Type it again',
  mismatch: 'The two entries are different. Please type it again.',
  refusals: {
    'too-short': 'Please use at least 12 characters, or 5 words.',
    'digits-length': 'Please use at least 12 characters, or 5 words.',
    repeated: setLock.refusals.repeated,
    sequence: setLock.refusals.sequence,
    common: setLock.refusals.common,
    date: setLock.refusals.date,
  } satisfies Record<SecretRefusal, string>,
  make: 'Make the backup file',
  making: 'Making the file…',
  made: 'Done. Your phone saved the file, usually in Downloads. Keep a copy somewhere safe, away from this phone.',
  honest: [
    `To restore, open ${PRODUCT_NAME} from the home screen of the new phone and choose “Restore from a backup file” at the start. Never open the file in a browser tab or on another website.`,
    'Anyone who has the file and its passphrase can open the diary. Without the passphrase, nobody can open it — not even you.',
    '“Delete everything” in this app does not reach backup files you saved elsewhere. Delete those yourself.',
  ],
} as const

export const restore = {
  entry: 'Restore from a backup file',
  title: 'Restore from a backup file',
  text: 'Choose the backup file, then type its backup passphrase. The diary is restored here as a new diary.',
  file: 'Backup file',
  passphrase: 'Backup passphrase',
  open: 'Open the backup',
  refusals: {
    'not-a-backup': `This file is not a ${PRODUCT_NAME} backup.`,
    damaged: 'This backup file is damaged, so it cannot be used.',
    'newer-version': `This backup was made by a newer version of ${PRODUCT_NAME}. Update the app, then try again.`,
    'weak-settings': 'This backup file uses settings that are not safe enough, so it was not opened.',
    'cannot-open': 'It did not open. Check the backup passphrase and try again. A file that was changed cannot be opened either.',
  } satisfies Record<BackupRefusal, string>,
  nothingChanged: 'Nothing was changed on this phone.',
  lockTitle: 'Choose a lock for this phone',
  lockText: 'The backup opened. Choose a lock for the diary on this phone. It may differ from the old one.',
  notEmpty: 'This phone already holds diary data, so the backup was not restored. A backup can be restored only into a new, empty diary.',
  full: 'There is not enough space on this phone to restore this backup. Nothing was written.',
} as const

export const unavailable = {
  text: `${PRODUCT_NAME} cannot reach its storage on this phone right now. Please close and reopen the app.`,
} as const

// Top bar + S11 Help now + S12 Hide (I-1a Batch 5; wording from wireframe v11
// S11/S12 and Tool Plan §12 / D-69 — any change is a help-line change: Tool
// Plan first, IM-D-08). "My people" (after unlock) and the medical panel are I-1b.
export const safety = {
  helpNow: 'Help now',
  hide: 'Hide',
  dangerTitle: 'Danger or medical emergency',
  talkTitle: 'Need to talk now',
  notEmergency: 'This is not an emergency service and nobody monitors it. You can reach out now:',
  linesTitle: 'Help lines',
  callNamed: (label: string, tel: string): string => `Call ${label} ${tel}`,
  call: (tel: string): string => `Call ${tel}`,
  message: 'Message',
  chat: { web: 'Open the chat (needs internet)', whatsapp: 'WhatsApp (needs internet)' } satisfies Record<
    NonNullable<HelpItem['chatKind']>,
    string
  >,
  /** The hours line under a card, e.g. "Phone: 24/7 · WhatsApp: not 24/7 · hours to verify". */
  hours: (item: HelpItem): string | null => {
    const parts: string[] = []
    if (item.tel && item.hours) {
      parts.push(`Phone: ${item.hours}`)
    }
    if (item.chatUrl && item.chatHours) {
      parts.push(`${item.chatKind === 'whatsapp' ? 'WhatsApp' : 'Chat'}: ${item.chatHours}`)
    }
    return parts.length > 0 ? parts.join(' · ') : null
  },
} as const
